import { isIOS } from './platform';

/** What iOS's "body" text style measures at the default Dynamic Type size. */
const IOS_DEFAULT_BODY_PX = 17;
const MIN_SCALE = 0.85;
const MAX_SCALE = 1.6;

/** The chat text scale for a measured iOS body size: 1 at the default, kept within sane limits. */
export function scaleFromBodyPx(bodyPx: number): number {
  if (!Number.isFinite(bodyPx) || bodyPx <= 0) return 1;
  const scale = bodyPx / IOS_DEFAULT_BODY_PX;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(scale * 100) / 100));
}

/**
 * Chat text follows the phone's text-size setting. On the web, chat sizes are in
 * rem, so the browser's own setting already applies. In the iPhone app the web view
 * ignores Dynamic Type, so measure iOS's "body" style and hand the ratio to CSS
 * (--text-scale, used by --chat-fs in tokens.css). Re-measured when the app comes
 * back to the front, which is when a changed setting takes effect. Android's web
 * view applies the phone's setting to rem by itself.
 */
export function startTextScale(): void {
  if (!isIOS || typeof document === 'undefined') return;
  try {
    if (!CSS.supports('font', '-apple-system-body')) return;
  } catch {
    return;
  }

  const apply = () => {
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;font:-apple-system-body;';
    probe.textContent = 'M';
    document.body.appendChild(probe);
    const px = parseFloat(getComputedStyle(probe).fontSize);
    probe.remove();
    document.documentElement.style.setProperty('--text-scale', String(scaleFromBodyPx(px)));
  };

  apply();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') apply();
  });
  window.addEventListener('focus', apply);
}
